import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import {
    CalendarEvent,
    CalendarView,
    DAYS_OF_WEEK,
    CalendarDateFormatter,
    CalendarEventAction,
    CalendarEventTimesChangedEvent
} from 'angular-calendar';
import { CommissionsService } from 'src/app/data/commission/services/commissions/commissions.service';
import { SessionContext } from 'src/app/core/services/config/app.settings';
import * as moment from 'moment';
import { CommissionAgenda } from 'src/app/data/commission/models/commission-agenda.model';
import { CustomDateFormatter } from './CustomDateFormatter.provider';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from 'src/app/shared/services/modal/modal.service';
import { AnimateursService } from 'src/app/data/commission/services/animateurs/animateurs.service';
import { Animateur } from 'src/app/data/commission/models/animateur.model';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { LoaderService } from '../../../../../core/services/loader/loader.service';

const colors: any = {
    red: { primary: '#ad2121', secondary: '#FAE3E3' },
    blue: { primary: '#1e90ff', secondary: '#D1E8FF' },
    yellow: { primary: '#FFFB00', secondary: '#FFFEBF' },
    orange: { primary: '#FFA500', secondary: '#FFE5B5' }
};

moment.updateLocale('fr', {
    week: {
        dow: DAYS_OF_WEEK.MONDAY,
        doy: 0
    }
});

@Component({
    selector: 'app-planning-commissions',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './planning-commissions.component.html',
    styleUrls: ['./planning-commissions.component.scss'],
    providers: [{ provide: CalendarDateFormatter, useClass: CustomDateFormatter }],
    standalone: false
})
export class PlanningCommissionsComponent implements OnInit {
    maxEventsMonthView = 3;
    view: CalendarView | 'year' = CalendarView.Week;
    CalendarView = CalendarView;
    viewDate: Date = new Date();
    refresh: Subject<void> = new Subject();
    events: CalendarEvent<{ commission: CommissionAgenda }>[] = [];
    activeDayIsOpen = false;
    dateDebut: moment.Moment = moment().startOf('week');
    dateFin: moment.Moment = moment().endOf('week');
    locale = 'fr_FR';
    weekStartsOn: number = DAYS_OF_WEEK.MONDAY;
    weekendDays: number[] = [DAYS_OF_WEEK.SATURDAY, DAYS_OF_WEEK.SUNDAY];
    actions: CalendarEventAction[] = [
        {
            label: 'Editer  ',
            onClick: ({ event }: { event: CalendarEvent }): void => {
                this.handleEvent('Edited', event);
            }
        },
        {
            label: '  Supprimer',
            onClick: ({ event }: { event: CalendarEvent }): void => {
                this.handleEvent('Deleted', event);
            }
        }
    ];
    animateurs: Animateur[];
    formFiltre: UntypedFormGroup;

    get listeEvents() {
        const filtreAnimateur = this.formFiltre?.get('animateur')?.value;

        if (!filtreAnimateur) {
            return this.events;
        }

        return this.events.filter(evt => evt.meta.commission.animateur?.id === filtreAnimateur.id);
    }

    get yearMonths(): Date[] {
        const year = this.viewDate.getFullYear();
        return Array.from({ length: 12 }, (_, index) => new Date(year, index, 1));
    }

    constructor(
        private readonly router: Router,
        private readonly commissionsService: CommissionsService,
        private readonly toastr: ToastrService,
        private readonly translate: TranslateService,
        private readonly modalsService: ModalService,
        private readonly animateursService: AnimateursService,
        private readonly fb: UntypedFormBuilder,
        private readonly loaderService: LoaderService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.formFiltre = this.fb.group({
            animateur: [null]
        });

        this.formFiltre.get('animateur').valueChanges.subscribe(() => {
            this.refresh.next();
        });

        this.loadData();
    }


    loadData() {
        forkJoin([
            this.commissionsService.getAgendaCommissions(
                SessionContext.get('idOrganisme'),
                this.dateDebut,
                this.dateFin
            ),
            this.animateursService.getAnimateurs(SessionContext.get('idOrganisme'))
        ])
            .pipe(this.loaderService.operator())
            .subscribe(([commissions, animateurs]) => {
                this.animateurs = animateurs ?? [];

                this.events = (commissions ?? []).map((commission: CommissionAgenda) => {
                    let titre = `<b>${this.translate.instant('page.commissions.planning.numero')} : ${commission.numero}</b><br />`;

                    if (this.view === CalendarView.Month) {
                        titre += `${this.translate.instant('page.commissions.planning.horaire')} : ${commission.debut.format('HH:mm')} - ${commission.fin.format('HH:mm')}<br />`;
                    }

                    titre += `- ${this.translate.instant('page.commissions.planning.salle')} : ${commission.salle?.nom ?? ''}<br />
                    - ${this.translate.instant('page.commissions.planning.animateur')} : ${commission.animateur?.prenom ?? ''} ${commission.animateur?.nom ?? ''}<br />
                    - ${this.translate.instant('page.commissions.planning.echantillons')} : ${commission.nbEchantillons}, ${this.translate.instant('page.commissions.planning.jures')} : ${commission.nbJuresPresents + commission.nbJuresInscrits} / ${commission.nbJuresTotal}`;

                    return {
                        title: titre,
                        start: commission.debut.toDate(),
                        end: commission.fin.toDate(),
                        resizable: { beforeStart: false, afterEnd: false },
                        draggable: false,
                        color: commission.terminee ? colors.orange : colors.yellow,
                        actions: this.actions,
                        meta: { commission }
                    } as CalendarEvent<{ commission: CommissionAgenda }>;
                });

                console.log('events construits =', this.events.length);

                this.refresh.next();
                this.cdr.markForCheck();
            });
    }
    
    compareAnimateurs(a: Animateur, b: Animateur) {
        return a && b ? a.id === b.id : a === b;
    }

    dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
        const dateJour = moment([date.getFullYear(), date.getMonth(), date.getDate()]);
        const dateVue = moment([this.viewDate.getFullYear(), this.viewDate.getMonth(), this.viewDate.getDate()]);

        if (dateVue.isSame(dateJour, 'month')) {
            if ((dateVue.isSame(dateJour, 'day') && this.activeDayIsOpen === true) || events.length === 0) {
                this.activeDayIsOpen = false;
            } else {
                this.activeDayIsOpen = true;
            }
            this.viewDate = date;
        }
    }

    eventTimesChanged({ event, newStart, newEnd }: CalendarEventTimesChangedEvent): void {
        this.events = this.events.map(iEvent => {
            if (iEvent === event) {
                return {
                    ...event,
                    start: newStart,
                    end: newEnd
                };
            }
            return iEvent;
        });

        this.handleEvent('Dropped or resized', event);
    }

    onHourSegmentClicked(event: { date: Date; sourceEvent?: MouseEvent }): void {
        const date = event.date;

        this.router.navigate(['main/controles/commissions/new/edit'], {
            queryParams: {
                 debut: moment(event.date).format('YYYY-MM-DDTHH:mm:ss')
            }
        });
    }

    handleEvent(action: string, event: CalendarEvent): void {
        if (action === 'Clicked' || action === 'Edited') {
            this.router.navigate([`main/controles/commissions/${event.meta.commission.id}/edit`]);
        } else if (action === 'Deleted') {
            if (
                event.meta.commission.nbEchantillons > 0 ||
                event.meta.commission.nbJuresTotal > 0 ||
                event.meta.commission.nbJuresInscrits > 0 ||
                event.meta.commission.nbJuresPresents > 0
            ) {
                this.modalsService.alert(this.translate.instant('page.commissions.planning.removeDisable'));
            } else {
                this.modalsService
                    .confirm(
                        `${this.translate.instant('page.commissions.planning.confirm_remove.message')} ${event.meta.commission.numero} ?`,
                        this.translate.instant('page.commissions.planning.confirm_remove.title'),
                        {
                            cancelText: this.translate.instant('label.annuler'),
                            submitText: this.translate.instant('label.valider')
                        }
                    )
                    .then(() => {
                        this.commissionsService.deleteCommission(SessionContext.get('idOrganisme'), event.meta.commission).subscribe(() => {
                            this.deleteEvent(event);
                            this.refresh.next();
                            this.toastr.success(
                                this.translate.instant('page.commissions.edit.alert.suppression.message', {
                                    numero: event.meta.commission.numero
                                }),
                                this.translate.instant('page.commissions.edit.alert.suppression.title'),
                                { timeOut: 10 * 1000 }
                            );
                        });
                    });
            }
        }
    }

    deleteEvent(eventToDelete: CalendarEvent) {
        this.events = this.events.filter(event => event !== eventToDelete);
    }

    setView(view: CalendarView | 'year') {
        this.view = view;
        this.refreshData();
    }

    openMonth(date: Date): void {
        this.viewDate = new Date(date);
        this.view = CalendarView.Month;
        this.activeDayIsOpen = false;
        this.refreshDatesIntervalle();
        this.loadData();
    }

    goToToday(): void {
            this.viewDate = new Date();
            this.activeDayIsOpen = false;
            this.refreshDatesIntervalle();
            this.loadData();
    }

    previousPeriod(): void {
        const newDate = new Date(this.viewDate);

        if (this.view === 'year') {
            newDate.setFullYear(newDate.getFullYear() - 1);
        } else if (this.view === CalendarView.Month) {
            newDate.setMonth(newDate.getMonth() - 1);
        } else if (this.view === CalendarView.Week) {
            newDate.setDate(newDate.getDate() - 7);
        } else {
            newDate.setDate(newDate.getDate() - 1);
        }

        this.viewDate = newDate;
        this.activeDayIsOpen = false;
        this.refreshDatesIntervalle();
        this.loadData();
    }

    nextPeriod(): void {
        const newDate = new Date(this.viewDate);

        if (this.view === 'year') {
            newDate.setFullYear(newDate.getFullYear() + 1);
        } else if (this.view === CalendarView.Month) {
            newDate.setMonth(newDate.getMonth() + 1);
        } else if (this.view === CalendarView.Week) {
            newDate.setDate(newDate.getDate() + 7);
        } else {
            newDate.setDate(newDate.getDate() + 1);
        }

        this.viewDate = newDate;
        this.activeDayIsOpen = false;
        this.refreshDatesIntervalle();
        this.loadData();
    }

    closeOpenMonthViewDay() {
        this.activeDayIsOpen = false;
        this.refreshDatesIntervalle();
        this.loadData();
    }

    refreshDatesIntervalle() {
        switch (this.view) {
            case 'year':
                this.dateDebut = moment([this.viewDate.getFullYear(), 0, 1]).startOf('year');
                this.dateFin = moment([this.viewDate.getFullYear(), 0, 1]).endOf('year');
                break;

            case CalendarView.Month:
                this.dateDebut = moment(this.viewDate).startOf('month');
                this.dateFin = moment(this.viewDate).endOf('month');
                break;

            case CalendarView.Week:
                this.dateDebut = moment(this.viewDate).startOf('week');
                this.dateFin = moment(this.viewDate).endOf('week');
                break;

            default:
                this.dateDebut = moment(this.viewDate).startOf('day');
                this.dateFin = moment(this.viewDate).endOf('day');
        }
    }

    refreshData() {
        this.refreshDatesIntervalle();
        this.loadData();
    }

    getEventsForMonth(monthDate: Date): CalendarEvent<{ commission: CommissionAgenda }>[] {
        const month = monthDate.getMonth();
        const year = monthDate.getFullYear();

        return this.listeEvents
            .filter(event => {
                const eventDate = new Date(event.start);
                return eventDate.getFullYear() === year && eventDate.getMonth() === month;
            })
            .sort((a, b) => a.start.getTime() - b.start.getTime());
    }

    get yearMonthsWithEvents() {
        const year = this.viewDate.getFullYear();

        return Array.from({ length: 12 }, (_, index) => {
            const date = new Date(year, index, 1);

            const events = this.listeEvents
                .filter(event => {
                    const eventDate = new Date(event.start);
                    return eventDate.getFullYear() === year && eventDate.getMonth() === index;
                })
                .sort((a, b) => a.start.getTime() - b.start.getTime());

            return { date, events };
        });
    }
}